import { CUSTOM_AGENTS_BASE, toLegacyPath } from '../../../pages/custom-agents/customAgentsRoutes';
import STATIC_MODULE_CATALOG from '../../../config/modules.generated';
import { canonicalModule, indexCatalog, licensedModuleSet } from '../../../licensing/moduleLicense';

export const GEMINI_ENTERPRISE_DASHBOARD_PATH = '/gemini/agents/dashboard';
export const GEMINI_WORKSPACE_DASHBOARD_PATH = '/gemini/dashboard';
export const GEMINI_ENTERPRISE_DASHBOARD_LABEL = 'Activation Dashboard';
export const GEMINI_WORKSPACE_DASHBOARD_LABEL = 'Workspace Dashboard';

// The Custom Agents Dashboard: one overview of the whole module (agents,
// activity, security, cost, compliance, assessment, alerts). The legacy
// /dashboard page is retired and forwards here; Observability is its own page
// at /observability. Nav and redirects all read the path from here.
export const CUSTOM_AGENTS_DASHBOARD_PATH = '/custom-agents/dashboard';

export const PRODUCTS = [
  {
    id: 'agent-atlas',
    label: 'Agent Atlas',
    shortLabel: 'Atlas',
    icon: 'globe',
    description: 'Enterprise-wide view of every AI agent',
    sections: ['agent-atlas'],
    routePrefixes: ['/agent-atlas/'],
    homePath: '/agent-atlas/atlas',
    // No feature flag: Agent Atlas is a core module (every tenant has it). The
    // AGENT_ATLAS flag that used to gate it was '*' and duplicated that.
  },
  {
    // Saf3AI's cross-cloud AI discovery surface. Powered by the
    // aws_cloudtrail / gcp_audit / azure_activity ingestors that write to
    // per-cloud OpenSearch indices (saf3ai-{aws,gcp,azure}-traces).
    id: 'cloud-ai',
    label: 'Cloud Discovery',
    shortLabel: 'Cloud',
    icon: 'cloud',
    description: 'Cross-cloud AI service & workload discovery',
    sections: ['cloud-ai'],
    routePrefixes: ['/cloud/'],
    homePath: '/cloud/overview',
  },
  {
    id: 'custom-agents',
    label: 'Custom Agents',
    shortLabel: 'Agents',
    icon: 'cpu',
    description: 'SDK/API AI monitoring & security',
    // One Gemini-style module tree. Entitlements still gate individual rows;
    // the historical sections remain for compatibility with other products.
    sections: ['custom-agents'],
    routePrefixes: [
      CUSTOM_AGENTS_BASE,
      '/agent-network', '/conversation-explorer', '/user-behavior',
      '/intent-analysis',
      '/observability', '/global-search',
      '/security/', '/reports', '/admin/compliance', '/admin/audit-report', '/admin/custom-guardrails',
      '/finops',
      '/model-evaluation', '/red-team-scanner',
      '/dashboard', '/admin/policies', '/admin/violations', '/admin/policy-observability', '/admin/agent-behavior', '/admin/policy-simulation',
      '/admin/policy-audit-log',
    ],
    // An entry route, not the page: /dashboard forwards to the Dashboard for
    // tenants that can open it and keeps the legacy page for compliance-only
    // tenants, who have no monitoring module (LegacyDashboardRoute, src/routes).
    homePath: '/dashboard',
  },
  {
    id: 'microsoft-copilot',
    label: 'Microsoft 365 Copilot',
    // The sidebar's product dropdown names it shorter; everywhere else keeps `label`.
    switcherLabel: 'Microsoft Copilot',
    shortLabel: 'Microsoft 365',
    icon: 'cpu',
    description: 'Microsoft 365 Copilot & Copilot Studio Governance',
    sections: ['enterprise-ai', 'copilot-studio'],
    routePrefixes: ['/copilot/'],
    homePath: '/copilot/dashboard',
  },
  {
    id: 'google-gemini',
    label: 'Google Gemini',
    shortLabel: 'Gemini',
    icon: 'sun',
    // Enterprise first + homePath so clicking Google Gemini lands on Enterprise,
    // not Workspace. Workspace stays visible but collapsed-by-default on switch
    // (see Sidebar handleProductChange) so customers can still open it on purpose.
    description: 'Workspace & Enterprise AI Governance',
    sections: ['gemini-enterprise-agents', 'gemini-workspace'],
    routePrefixes: ['/gemini/'],
    homePath: GEMINI_ENTERPRISE_DASHBOARD_PATH,
  },
  // Unified SaaS-AI module — one surface for every hosted-API AI vendor
  // (OpenAI, Anthropic, … selected via a ?vendor= switcher). Replaces the
  // per-vendor OpenAI product. Entitlement-gated only (like Copilot/Gemini):
  // visible to any org with the openai or anthropic module.
  {
    id: 'saas-ai',
    label: 'AI Providers',
    shortLabel: 'AI Providers',
    icon: 'zap',
    description: 'Hosted-API AI governance — OpenAI, Anthropic & Cursor',
    sections: ['saas-ai-platform'],
    routePrefixes: ['/saas-ai/'],
    homePath: '/saas-ai/dashboard',
  },
  {
    id: 'endpoint-security',
    label: 'Endpoint Security',
    shortLabel: 'Endpoint',
    icon: 'eye',
    description: 'AI use on every endpoint — browser, managed device and egress',
    // Only the consolidated module. The four surfaces it replaces — Browser
    // Endpoint, Managed Devices, EDR and AI Gateway — are dropped from this
    // list, which is the single gate isSectionVisibleForProduct reads, so all
    // four headings and every link under them disappear together.
    //
    // Their ROUTES stay live and stay in routePrefixes below: a bookmark and
    // a deep link keep working, and the product stays highlighted when one is
    // opened. This hides the navigation, it does not remove the capability.
    sections: ['shadow-ai'],
    routePrefixes: [
      '/admin/endpoint-settings', '/admin/endpoint-violations', '/admin/endpoint-alerts',
      '/admin/fleet-overview', '/admin/shadow-ai-discovery',
      '/admin/dlp-analytics', '/admin/endpoint-user-activity',
      '/admin/ai-tool-catalog', '/admin/ai-conversations',
      '/admin/ai-gateway',
      // Managed Devices (Jamf / Intune) — the agentless discovery surface.
      '/admin/mdm',
      // EDR — CrowdStrike Falcon + Microsoft Defender for Endpoint.
      '/admin/edr',
      // The consolidated module that replaces the four above.
      '/endpoint-security',
      // Its former path. Kept so an old bookmark still highlights this
      // product on the way through the redirect, rather than briefly
      // selecting another one.
      '/shadow-ai',
    ],
    // The consolidated module's Overview, not the Browser Endpoint fleet page
    // it replaces. Clicking the product took you to one of the four surfaces
    // being folded in rather than to the thing folding them.
    homePath: '/endpoint-security',
  },
  {
    id: 'code-security',
    label: 'Code Security',
    shortLabel: 'SAST',
    icon: 'code',
    description: 'SAST & SCA code scanning',
    sections: ['sast'],
    routePrefixes: ['/sast/'],
    homePath: '/sast/overview',
  },
];

export const ALWAYS_VISIBLE_SECTIONS = [
  'management',
];

/**
 * Sidebar section → the module slugs it belongs to, from the module catalog's
 * `ui_sections` (saf3ai-auth-sdk modules.yaml — the single source of truth;
 * config/modules.generated.js is its generated copy, kept in sync by test).
 * A section in no module is never locked.
 *
 * Sections are no longer HIDDEN when the org lacks their module: they show
 * with a lock, and their pages open on the lock screen (licensing/,
 * components/licensing/ModuleLocked). Use useModuleLicense().isSectionLicensed
 * for that decision; this map is kept for callers that only need the slugs.
 */
export const sectionModuleSlugsFrom = (index) => Object.fromEntries(
  [...index.sections.entries()].map(([key, slugs]) => [key, [...slugs]]),
);

const STATIC_MODULE_INDEX = indexCatalog(STATIC_MODULE_CATALOG);

export const SECTION_MODULE_SLUGS = Object.freeze(sectionModuleSlugsFrom(STATIC_MODULE_INDEX));

/**
 * The modules an org's `modules` claim licenses: canonical slugs (a former slug such as
 * `compliance` counts as its module, `governance`) plus every core module, which the
 * claim never carries (Agent Atlas, Policy, and every data source).
 */
export const entitledModuleSet = (modules) => licensedModuleSet(modules || [], STATIC_MODULE_INDEX);

/** Whether `set` (entitledModuleSet) holds `slug`, in any of its spellings. */
export const holdsModule = (set, slug) => set.has(canonicalModule(slug, STATIC_MODULE_INDEX) || slug);

/** Axon AI entry uses the same rough slice as core agent governance. */
export const AXON_AI_MODULE_SLUGS = ['monitoring', 'governance'];

export function shouldApplyModuleEntitlements(modules) {
  return Array.isArray(modules) && modules.length > 0;
}

export function parseEntitledSlugSet(modules) {
  const set = new Set();
  if (!Array.isArray(modules)) return set;
  modules.forEach((s) => {
    if (s != null && String(s).trim() !== '') {
      set.add(String(s).trim().toLowerCase());
    }
  });
  return set;
}

export function isSectionEntitled(sectionKey, entitledSlugSet) {
  if (ALWAYS_VISIBLE_SECTIONS.includes(sectionKey)) return true;
  const required = SECTION_MODULE_SLUGS[sectionKey];
  if (!required || required.length === 0) return true;
  return required.some((slug) => entitledSlugSet.has(String(slug).toLowerCase()));
}

export function isAxonAiEntitled(entitledSlugSet) {
  return AXON_AI_MODULE_SLUGS.some((slug) => entitledSlugSet.has(slug.toLowerCase()));
}

/**
 * Products the switcher lists: every product whose feature flag (if any)
 * passes. Module licensing no longer removes a product — an unlicensed one is
 * listed with a lock (isProductLicensed) and opens on its lock screen. The
 * entitlement arguments are kept for existing callers and ignored.
 */
// eslint-disable-next-line no-unused-vars
export function getVisibleProducts(_entitledSlugSet, _applyEntitlements) {
  // Lazy import to keep this module side-effect-free at load time.
  // eslint-disable-next-line global-require
  const { isFeatureEnabled } = require('../../../utils/featureFlags');
  return PRODUCTS.filter((product) => !product.featureFlag || isFeatureEnabled(product.featureFlag));
}

/** A product is licensed when the org holds the module of any of its sections. */
export function isProductLicensed(product, license) {
  if (!product || !license) return true;
  return product.sections.some((sectionKey) => license.isSectionLicensed(sectionKey));
}

export function getProductHomePath(product, entitledSlugSet) {
  if (product?.id === 'microsoft-copilot' &&
      !entitledSlugSet.has('copilot') && entitledSlugSet.has('copilot_studio')) {
    return '/copilot/studio/dashboard';
  }
  return product?.homePath;
}

export const LOCALSTORAGE_KEY = 'sidebar_selected_product';

export function normalizeStoredProductId(productId) {
  // Migrate the temporarily-split Enterprise/Workspace products (from a
  // since-reverted change) back to the single combined Google Gemini
  // product, so a stale localStorage value from that window still resolves.
  if (productId === 'gemini-enterprise' || productId === 'gemini-workspace') {
    return 'google-gemini';
  }
  return productId;
}

/**
 * Match a pathname against a route prefix on a path-segment boundary, so e.g.
 * '/admin/policy-audit' doesn't swallow '/admin/policy-audit-log'.
 *
 * Valid match: pathname === prefix, pathname = prefix + '/' + rest, or (for
 * prefixes that already end with '/') a plain startsWith.
 */
function matchesRoutePrefix(pathname, prefix) {
  if (prefix === '/dashboard') return pathname === '/dashboard';
  if (pathname === prefix) return true;
  if (prefix.endsWith('/')) return pathname.startsWith(prefix);
  return pathname.startsWith(prefix + '/');
}

export function detectProductFromPath(pathname) {
  for (const product of PRODUCTS) {
    if (product.routePrefixes.some(prefix => matchesRoutePrefix(pathname, prefix))) {
      return product.id;
    }
  }
  return null;
}

export function getGeminiNavigationVisibility(pathname) {
  const enterprise = matchesRoutePrefix(pathname, '/gemini/agents');
  const workspace = !enterprise && matchesRoutePrefix(pathname, '/gemini');

  return {
    enterprise: !workspace,
    workspace: !enterprise,
  };
}

/**
 * Routes that sit under a product which shows the global agent/time filter, but
 * whose pages don't read it.
 *
 * These are scan-centric surfaces: a red-team scan or a model evaluation is
 * scoped to a target endpoint and a scan run, not to an agent over a rolling
 * time window. The header filter rendered on them but nothing consumed it, so
 * changing it appeared to do nothing — dead UI that implied the page was
 * filtered when it wasn't.
 *
 * Adding a route here only hides the header control. If one of these pages
 * later starts consuming the global filter, remove its prefix.
 */
export const GLOBAL_FILTER_HIDDEN_ROUTE_PREFIXES = [
  '/agent-atlas',        // Agent Atlas renders its own date-range controls.
  '/red-team-scanner',
  '/red-team',           // covers /red-team/scheduled-scans
  '/model-evaluation',   // covers /projects, /arena/:id, /clone/:id
  '/intent-analysis',    // page renders its own filter bar (date + agent/user)
  '/custom-agents/dashboard', // renders its own date-range control
];

/** True when the global agent/time filter should be hidden for this route. */
export function isGlobalFilterHiddenForPath(pathname) {
  // A canonical /custom-agents URL follows the rule of the page it mirrors.
  const path = toLegacyPath(pathname);
  return GLOBAL_FILTER_HIDDEN_ROUTE_PREFIXES.some(prefix => matchesRoutePrefix(path, prefix));
}

export function isSectionVisibleForProduct(sectionKey, selectedProductId) {
  if (ALWAYS_VISIBLE_SECTIONS.includes(sectionKey)) return true;
  const product = PRODUCTS.find(p => p.id === selectedProductId);
  if (!product) return true;
  return product.sections.includes(sectionKey);
}
